import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import api from 'utils/api';
import { DOLPHIN_API } from 'shared/constants/apis';

const AlertSeverity = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

const LLMPrivileges = () => {
  const [organizations, setOrganizations] = useState([]);
  const [organizationPrivileges, setOrganizationPrivileges] = useState({});
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentEditOrg, setCurrentEditOrg] = useState(null);
  const [editedJson, setEditedJson] = useState('');
  const [jsonError, setJsonError] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: AlertSeverity.SUCCESS,
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const orgs = await api.get(DOLPHIN_API.ADMIN_ORGS, {});
      setOrganizations(orgs);

      if (orgs.length > 0) {
        await fetchOrganizationPrivileges(orgs);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch organizations',
        severity: AlertSeverity.ERROR,
      });
      setLoading(false);
    }
  };

  const fetchOrganizationPrivileges = async (orgs) => {
    try {
      const orgIds = orgs.map((org) => org.id);
      // Using the correct API path and passing organizationIds as a param
      const response = await api.get('/narwhal/privileges', {
        params: {
          organizationIds: JSON.stringify(orgIds),
        },
      });

      setOrganizationPrivileges(response);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching organization privileges:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch organization privileges',
        severity: AlertSeverity.ERROR,
      });
      setLoading(false);
    }
  };

  const handleToggleAIFeature = async (orgId, orgCode, currentValue) => {
    try {
      const newValue = !currentValue;

      // Update local state optimistically
      setOrganizationPrivileges((prev) => {
        const updatedPrivileges = { ...prev };
        if (updatedPrivileges[orgId]?.privilege) {
          updatedPrivileges[orgId].privilege.AIFeatureEnabled = newValue;
        }
        return updatedPrivileges;
      });

      const result = await api.put('/narwhal/privilege', {
        params: {
          organizationId: orgId,
          organizationCode: orgCode,
        },
        data: {
          AIFeatureEnabled: newValue,
          privileges:
            organizationPrivileges[orgId]?.privilege?.privileges || {},
        },
      });

      // Update state with result from API
      if (result && result.privilege) {
        setOrganizationPrivileges((prev) => {
          const updatedPrivileges = { ...prev };
          updatedPrivileges[orgId] = {
            message: result.message || 'Privilege found',
            privilege: result.privilege,
          };
          return updatedPrivileges;
        });
      }

      setSnackbar({
        open: true,
        message: `AI Feature ${newValue ? 'enabled' : 'disabled'} successfully`,
        severity: AlertSeverity.SUCCESS,
      });
    } catch (error) {
      console.error('Error updating AI feature:', error);

      // Revert the optimistic update
      setOrganizationPrivileges((prev) => {
        const updatedPrivileges = { ...prev };
        if (updatedPrivileges[orgId]?.privilege) {
          updatedPrivileges[orgId].privilege.AIFeatureEnabled = currentValue;
        }
        return updatedPrivileges;
      });

      setSnackbar({
        open: true,
        message: 'Failed to update AI feature',
        severity: AlertSeverity.ERROR,
      });
    }
  };

  const handleEditPrivileges = (org) => {
    setCurrentEditOrg(org);
    const currentPrivileges =
      organizationPrivileges[org.id]?.privilege?.privileges || {};
    setEditedJson(JSON.stringify(currentPrivileges, null, 2));
    setEditDialogOpen(true);
  };

  const handleSavePrivileges = async () => {
    try {
      // Validate JSON
      const parsedJson = JSON.parse(editedJson);

      // Prepare request data
      const requestData = {
        AIFeatureEnabled:
          organizationPrivileges[currentEditOrg.id]?.privilege
            ?.AIFeatureEnabled || false,
        privileges: parsedJson,
      };

      // Send update to server
      await api.put('/narwhal/privilege', {
        params: {
          organizationId: currentEditOrg.id,
          organizationCode: currentEditOrg.organizationCode,
        },
        data: requestData,
      });

      // Update local state
      setOrganizationPrivileges((prev) => {
        const updatedPrivileges = { ...prev };
        if (updatedPrivileges[currentEditOrg.id]?.privilege) {
          updatedPrivileges[currentEditOrg.id].privilege.privileges =
            parsedJson;
        } else if (updatedPrivileges[currentEditOrg.id]) {
          updatedPrivileges[currentEditOrg.id].privilege = {
            AIFeatureEnabled: false,
            privileges: parsedJson,
          };
        } else {
          updatedPrivileges[currentEditOrg.id] = {
            message: 'Privilege found',
            privilege: {
              AIFeatureEnabled: false,
              privileges: parsedJson,
            },
          };
        }
        return updatedPrivileges;
      });

      setSnackbar({
        open: true,
        message: 'Privileges updated successfully',
        severity: AlertSeverity.SUCCESS,
      });

      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating privileges:', error);
      setJsonError(true);
      setSnackbar({
        open: true,
        message: 'Invalid JSON format',
        severity: AlertSeverity.ERROR,
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleJsonChange = (event) => {
    setEditedJson(event.target.value);
    // Reset error state when editing
    setJsonError(false);
  };

  return (
    <Box pt={6} pb={3}>
      <Box mb={3}>
        <Card>
          <Box p={3}>
            <Typography variant="h5" fontWeight="medium">
              LLM Privileges Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage AI feature access and privileges for organizations
            </Typography>
          </Box>
          <Box p={3}>
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table
                  sx={{ minWidth: 650 }}
                  aria-label="organization privileges table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Organization ID</TableCell>
                      <TableCell>Organization Name</TableCell>
                      <TableCell>AI Feature Enabled</TableCell>
                      <TableCell>Privileges</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {organizations.map((org) => {
                      const orgPrivilegeData = organizationPrivileges[org.id];
                      const privilege = orgPrivilegeData?.privilege;
                      const AIFeatureEnabled =
                        privilege?.AIFeatureEnabled || false;

                      return (
                        <TableRow key={org.id}>
                          <TableCell>{org.id}</TableCell>
                          <TableCell>{org.name}</TableCell>
                          <TableCell>
                            <Switch
                              checked={AIFeatureEnabled}
                              onChange={() =>
                                handleToggleAIFeature(
                                  org.id,
                                  org.organizationCode,
                                  AIFeatureEnabled
                                )
                              }
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            {privilege ? (
                              <Box
                                sx={{
                                  maxHeight: '100px',
                                  maxWidth: '300px',
                                  overflow: 'auto',
                                  whiteSpace: 'pre-wrap',
                                  fontFamily: 'monospace',
                                  fontSize: '0.8rem',
                                  p: 1,
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '4px',
                                  bgcolor: '#f5f5f5',
                                }}>
                                {JSON.stringify(
                                  privilege.privileges || {},
                                  null,
                                  2
                                )}
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary">
                                No privileges defined
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {privilege ? (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleEditPrivileges(org)}
                                sx={{
                                  backgroundColor: 'primary.main',
                                  color: '#ffffff',
                                  fontWeight: 'bold',
                                  '&:hover': {
                                    backgroundColor: 'primary.dark',
                                  },
                                }}>
                                Edit Privileges
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Card>
      </Box>

      {/* Edit Privileges Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth>
        <DialogTitle>
          Edit Privileges for {currentEditOrg?.name || ''}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={12}
            value={editedJson}
            onChange={handleJsonChange}
            error={jsonError}
            helperText={jsonError ? 'Invalid JSON format' : ''}
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
              },
            }}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSavePrivileges}
            variant="contained"
            color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LLMPrivileges;
